import React, { useEffect, useMemo } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import {
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    SimpleGrid,
    useBoolean,
    HStack,
    VStack,
    Button,
    FormLabel,
    IconButton,
    Text,
} from '@chakra-ui/react';
import { Form, renderField } from '~/components/Form/Form';
import { fields, forms } from '~/pages/projects/meta/data/documents.schema';
import { resolveFormFields, withRenderers } from '~/utils';
import { useModal } from '~/hooks';
import { FiDownload } from 'react-icons/fi';
import { ListOffersDocument, SortOrder } from '~/gql/ucpw/graphql';
import { tables } from '~/pages/projects/meta/data/finance.schema';
import { DataTable } from '@ucc/react/ui';
import { createColumnHelper } from '@tanstack/react-table';
import { usePdf } from '~/pages/projects/hooks/usePdf';
import { useProjectIds } from '../hooks/useProjectIds';

const columnHelper = createColumnHelper<any>();

export const WorkdescriptionModal = (props?: { id?: string }) => {
    const { projectId, subprojectId } = useProjectIds();
    const pdf: any = usePdf({ projectId, subprojectId });
    const [isLoading, setIsLoading] = useBoolean(false);
    const { onClose, dispatch, state } = useModal();
    const offer = state?.modals?.props || {};
    const [selectedOffer, setSelectedOffer] = React.useState<any>(null);
    const [activeRow, setActiveRow] = React.useState<any>('');
    const formFields = resolveFormFields(forms.workdescription, fields.workdescription);
    const {
        handleSubmit,
        control,
        register,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {},
    });

    const { data, loading } = useQuery(ListOffersDocument, {
        variables: {
            filter: { subprojektId: subprojectId },
            orderBy: { datum: SortOrder.Desc },
        },
        skip: !Boolean(subprojectId),
        context: { clientName: 'ucpw' },
        fetchPolicy: 'network-only',
    });
    const offers = useMemo(() => {
        const offers = data?.items?.items || [];
        return offer?.id ? offers.filter((o: any) => o?.id === offer?.id) : offers;
    }, [data, offer]);

    const columns = [
        ...withRenderers(tables.offers.columns),
        columnHelper.accessor('angebotspositionen', {
            header: 'Angebotspositionen',
            cell: (info) => {
                const positions = info.getValue() || [];
                return (
                    <VStack align="start">
                        {positions.map((pos: any) => (
                            <HStack key={pos.id}>
                                <Text alignSelf="self-start" fontWeight="bold">
                                    {pos.lfdNr}:
                                </Text>
                                <Text>
                                    {pos?.rahmenvertragspositionId
                                        ? pos?.rahmenvertragsposition?.beschreibung
                                        : pos?.beschreibung}
                                </Text>
                            </HStack>
                        ))}
                    </VStack>
                );
            },
        }),
    ];

    const onReset = () => {
        dispatch?.({ type: 'resetState' });
        reset({});
        onClose?.();
    };

    const onSubmit: SubmitHandler<any> = React.useCallback(
        async (values) => {
            console.log('onSubmit', { values, selectedOffer });
            await pdf.workDescription({
                ...selectedOffer,
                freitext: { raw: JSON.stringify(values?.workdescriptionText || []) },
            });
            onReset();
        },
        [selectedOffer, onReset]
    );

    const onSubmitWithOnClose = async (values: any) => {
        setIsLoading.on();
        await onSubmit(values);
        setIsLoading.off();
        onReset();
    };

    useEffect(() => {
        if (offers.length === 1) {
            setSelectedOffer(offers[0]);
            setActiveRow('0');
        }
    }, [offers]);

    return (
        <ModalContent rounded="none" maxWidth="container.md">
            <ModalHeader
                p={5}
                justifyContent="space-between"
                alignItems="center"
                display="flex"
                borderBottomWidth={1}
                borderColor="gray.200"
            >
                Arbeitsbeschreibung
            </ModalHeader>
            <ModalBody>
                <Form onSubmit={handleSubmit(onSubmitWithOnClose)}>
                    <SimpleGrid spacing={4} columns={1} mb={4}>
                        {formFields.fields.map((field: any) => (
                            <React.Fragment key={field.name}>
                                {renderField({
                                    field,
                                    control,
                                    register,
                                    errors,
                                    context: {},
                                })}
                            </React.Fragment>
                        ))}
                    </SimpleGrid>
                </Form>

                {selectedOffer ? (
                    <FormLabel>{`Angebot ausgewählt (${selectedOffer.id})`}</FormLabel>
                ) : (
                    <FormLabel>Angebot auswählen</FormLabel>
                )}
                <DataTable
                    key={`activeRow:${activeRow}`}
                    data={offers}
                    columns={columns}
                    loading={loading}
                    loadingLength={2}
                    onClick={(row) => setSelectedOffer(row?.original)}
                    hiddenColumns={['angebotspositionen']}
                    activeRow={activeRow}
                    activeChecked
                />
            </ModalBody>
            <ModalFooter>
                <HStack>
                    <Button data-test-id="button-cancel" variant="outline" onClick={onReset}>
                        Abbrechen
                    </Button>
                    <IconButton
                        ml={3}
                        isDisabled={!selectedOffer}
                        aria-label="download-icon-button"
                        colorScheme="blue"
                        icon={<FiDownload />}
                        isLoading={isLoading}
                        onClick={handleSubmit(onSubmitWithOnClose)}
                    />
                </HStack>
            </ModalFooter>
        </ModalContent>
    );
};
